<template>
    <div
        class="ppv2-w-full ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-bg-white dark:ppv2-bg-dark ppv2-px-[14px] ppv2-py-3 ppv2-cursor-pointer ppv2-select-none hover:ppv2-bg-black hover:ppv2-bg-opacity-5 ppv2-transition-colors"
        @click="$emit('toggle')"
    >
        <div
            class="bg-purple ppv2-bg-opacity-10 ppv2-shrink-0 ppv2-w-6 ppv2-h-6 ppv2-rounded-[7px] ppv2-flex ppv2-items-center ppv2-justify-center poppinsSemiBold"
            style="color: #7d2dbd"
            :style="{ fontSize: normalizeFontSize(12) }"
        >
            {{ index + 1 }}
        </div>

        <div
            class="ppv2-bg-black ppv2-bg-opacity-5 ppv2-shrink-0 ppv2-w-[26px] ppv2-h-[26px] ppv2-rounded-[7px] ppv2-flex ppv2-items-center ppv2-justify-center"
            style="border: 1px solid #eef1f5; color: #6b7480"
        >
            <FormTypeIcon :name="question.typeKey" :size="15" />
        </div>

        <span
            class="poppinsMedium ppv2-flex-1 ppv2-min-w-0 ppv2-truncate"
            :class="question.label ? 'ppv2-text-black' : 'ppv2-text-gray-400'"
            :style="{ fontSize: normalizeFontSize(14) }"
        >
            {{ question.label || $t('forms.question.emptyTitle') }}
        </span>

        <span
            class="poppinsRegular ppv2-shrink-0 ppv2-rounded-md ppv2-max-w-[180px] ppv2-truncate ppv2-px-2 ppv2-py-[3px] ppv2-bg-black ppv2-bg-opacity-5"
            style="border: 1px solid #eef1f5; color: #6b7480"
            :style="{ fontSize: normalizeFontSize(12) }"
        >
            {{ summary }}
        </span>

        <span
            v-if="question.required"
            class="poppinsMedium ppv2-shrink-0 ppv2-rounded-md ppv2-px-2 ppv2-py-[3px]"
            style="background: #f3eafa; color: #571f84"
            :style="{ fontSize: normalizeFontSize(11.5) }"
        >
            {{ $t('forms.question.required') }}
        </span>

        <div class="ppv2-shrink-0 ppv2-flex ppv2-items-center ppv2-gap-0.5 ppv2-ml-1" @click.stop>
            <button
                type="button"
                class="ppv2-w-7 ppv2-h-7 ppv2-rounded-[7px] ppv2-flex ppv2-items-center ppv2-justify-center ppv2-text-black ppv2-text-opacity-60 hover:ppv2-bg-[#7d2dbd] hover:ppv2-bg-opacity-10 ppv2-transition-colors disabled:ppv2-opacity-30 disabled:ppv2-cursor-not-allowed disabled:hover:ppv2-bg-transparent"
                :disabled="isFirst"
                :title="$t('forms.question.moveUp')"
                @click="$emit('move', -1)"
            >
                <FormTypeIcon name="arrow-up" :size="16" />
            </button>
            <button
                type="button"
                class="ppv2-w-7 ppv2-h-7 ppv2-rounded-[7px] ppv2-flex ppv2-items-center ppv2-justify-center ppv2-text-black ppv2-text-opacity-60 hover:ppv2-bg-[#7d2dbd] hover:ppv2-bg-opacity-10 ppv2-transition-colors disabled:ppv2-opacity-30 disabled:ppv2-cursor-not-allowed disabled:hover:ppv2-bg-transparent"
                :disabled="isLast"
                :title="$t('forms.question.moveDown')"
                @click="$emit('move', 1)"
            >
                <FormTypeIcon name="arrow-down" :size="16" />
            </button>
            <button
                type="button"
                class="ppv2-w-7 ppv2-h-7 ppv2-rounded-[7px] ppv2-flex ppv2-items-center ppv2-justify-center ppv2-text-black ppv2-text-opacity-60 hover:ppv2-bg-[#7d2dbd] hover:ppv2-bg-opacity-10 ppv2-transition-colors disabled:ppv2-opacity-30 disabled:ppv2-cursor-not-allowed disabled:hover:ppv2-bg-transparent"
                :disabled="question.locked"
                :title="question.locked ? $t('forms.question.locked') : $t('forms.question.duplicate')"
                @click="$emit('duplicate')"
            >
                <FormTypeIcon name="copy" :size="16" />
            </button>
            <button
                type="button"
                class="ppv2-w-7 ppv2-h-7 ppv2-rounded-[7px] ppv2-flex ppv2-items-center ppv2-justify-center ppv2-text-black ppv2-text-opacity-60 hover:ppv2-bg-[#fdeceb] hover:ppv2-text-[#c0392b] ppv2-transition-colors disabled:ppv2-opacity-30 disabled:ppv2-cursor-not-allowed disabled:hover:ppv2-bg-transparent"
                :disabled="question.locked"
                :title="question.locked ? $t('forms.question.locked') : $t('forms.question.delete')"
                @click="$emit('delete')"
            >
                <FormTypeIcon name="trash" :size="16" />
            </button>
        </div>

        <div class="ppv2-shrink-0 ppv2-w-[26px] ppv2-flex ppv2-items-center ppv2-justify-center" style="color: #cbd2dc">
            <FormTypeIcon
                name="chevron-down"
                :size="16"
                class="ppv2-transition-transform"
                :class="expanded ? 'ppv2-rotate-180' : ''"
            />
        </div>
    </div>
</template>

<script>
import normalizeFontSize from '../../../../utils/normalizeFontSize'
import { plural } from '../../../../utils/plural'
import FormTypeIcon from './FormTypeIcon.vue'
import { FORM_TYPES } from '../formTypes'

export default {
    name: 'FormQuestionRow',
    components: { FormTypeIcon },
    props: {
        question: {
            type: Object,
            required: true,
        },
        expanded: {
            type: Boolean,
            default: false,
        },
        index: {
            type: Number,
            default: 0,
        },
        isFirst: {
            type: Boolean,
            default: false,
        },
        isLast: {
            type: Boolean,
            default: false,
        },
    },
    computed: {
        summary() {
            const q = this.question
            const t = (path, params) => this.$t(path, params)
            const typeKey = FORM_TYPES[q.typeKey] ? q.typeKey : 'short'
            const parts = [this.$t(FORM_TYPES[typeKey].label)]
            switch (q.typeKey) {
                case 'short':
                    break
                case 'long':
                    if (q.max) parts.push(t('forms.question.summary.maxLength', { n: q.max }))
                    break
                case 'number': {
                    const hasMin = q.min !== '' && q.min !== null
                    const hasMax = q.max !== '' && q.max !== null
                    if (hasMin && hasMax) parts.push(t('forms.question.summary.minMax', { min: q.min, max: q.max }))
                    else if (hasMin) parts.push(`≥ ${q.min}`)
                    else if (hasMax) parts.push(`≤ ${q.max}`)
                    break
                }
                case 'radio':
                case 'select':
                case 'experience':
                    parts.push(
                        t('forms.question.summary.answers', { n: q.options.length, plural: plural(q.options.length, '', 's') })
                    )
                    break
                case 'checkbox':
                    parts.push(
                        t('forms.question.summary.answers', { n: q.options.length, plural: plural(q.options.length, '', 's') })
                    )
                    parts.push(t('forms.question.summary.minChoices', { n: q.minChoices }))
                    break
                case 'date':
                    return q.dateFormat === 'range'
                        ? t('forms.question.summary.dateRange')
                        : t('forms.question.summary.dateSingle')
                default:
                    break
            }
            return parts.join(' · ')
        },
    },
    methods: {
        normalizeFontSize,
    },
}
</script>
